"use client";

import { useCallback, useState } from "react";
import type { PositionBroker } from "@features/api/memberApi";
import type {
  PlaidAccountCategory,
  PlaidConnectFlowOptions,
  PlaidFlowState,
  PlaidFlowStep,
} from "./types";

export const resolveShowAddManually = (
  options: Pick<PlaidConnectFlowOptions, "showAddManually" | "fromOnboarding">,
): boolean => options.showAddManually ?? !Boolean(options.fromOnboarding);

export const usePlaidConnectFlowMachine = (
  options: PlaidConnectFlowOptions = {},
) => {
  const getInitialStep = (): PlaidFlowStep => {
    if (options.fromOnboarding && !options.skipDenseWall) {
      return "DENSE_WALL";
    }
    if (options.accountType === "invest") {
      return "INVEST_LIST";
    }
    if (options.accountType === "bank") {
      return "BANK_LIST";
    }
    return "CATEGORY_SELECT";
  };

  const [state, setState] = useState<PlaidFlowState>(() => {
    const initialStep = getInitialStep();
    return {
      currentStep: initialStep,
      stepHistory: [initialStep],
      accountType: options.accountType,
      fromOnboarding: Boolean(options.fromOnboarding),
      showAddManually: resolveShowAddManually(options),
      hasSelectedPortfolio: Boolean(options.hasSelectedPortfolio),
      isPlaidConnecting: false,
    };
  });

  const navigateTo = useCallback((nextStep: PlaidFlowStep) => {
    setState((prev) => ({
      ...prev,
      currentStep: nextStep,
      stepHistory: [...prev.stepHistory, nextStep],
    }));
  }, []);

  const goBack = useCallback(() => {
    setState((prev) => {
      if (prev.stepHistory.length <= 1) {
        return prev;
      }
      const newHistory = prev.stepHistory.slice(0, -1);
      return {
        ...prev,
        currentStep: newHistory[newHistory.length - 1],
        stepHistory: newHistory,
      };
    });
  }, []);

  /**
   * 挽留 CTA「Connect account」：
   * - 来源是 P5 手动上传 → 落到 P2 类别选择（不是回到上传页）
   * - 其它来源 → 普通 goBack
   */
  const continueConnectFromRetention = useCallback(() => {
    setState((prev) => {
      if (prev.stepHistory.length <= 1) {
        return prev;
      }
      const withoutRetention = prev.stepHistory.slice(0, -1);
      const previous = withoutRetention[withoutRetention.length - 1];
      if (previous === "MANUAL_UPLOAD") {
        const history: PlaidFlowStep[] =
          withoutRetention[0] === "DENSE_WALL"
            ? ["DENSE_WALL", "CATEGORY_SELECT"]
            : ["CATEGORY_SELECT"];
        return {
          ...prev,
          currentStep: "CATEGORY_SELECT",
          stepHistory: history,
          accountType: undefined,
          selectedInstitution: undefined,
        };
      }
      return {
        ...prev,
        currentStep: previous,
        stepHistory: withoutRetention,
      };
    });
  }, []);

  const triggerExitRetention = useCallback(() => {
    setState((prev) => {
      const retentionStep: PlaidFlowStep = prev.hasSelectedPortfolio
        ? "EXIT_RETENTION_PORTFOLIO"
        : "EXIT_RETENTION_GENERAL";
      return {
        ...prev,
        currentStep: retentionStep,
        stepHistory: [...prev.stepHistory, retentionStep],
      };
    });
  }, []);

  const setAccountType = useCallback((type: PlaidAccountCategory) => {
    setState((prev) => ({ ...prev, accountType: type }));
  }, []);

  const setSelectedInstitution = useCallback((broker?: PositionBroker) => {
    setState((prev) => ({ ...prev, selectedInstitution: broker }));
  }, []);

  const setPlaidConnecting = useCallback((isConnecting: boolean) => {
    setState((prev) => ({ ...prev, isPlaidConnecting: isConnecting }));
  }, []);

  return {
    state,
    navigateTo,
    goBack,
    continueConnectFromRetention,
    triggerExitRetention,
    setAccountType,
    setSelectedInstitution,
    setPlaidConnecting,
  };
};
